import React, { useState, useEffect, useRef } from "react";
import { FaPlay, FaPause } from "react-icons/fa";
import { MdOutlineRestartAlt } from "react-icons/md";
import Popup from "./Popup"; // Assuming Popup is in the same directory
import { updateDocumentInSubcollection } from "../firestore_sub"; // Import update function
import { auth } from "../firebase"; // Import auth for uid access
import { updateDocument, getDocument } from "../firestore";
import GameOverScreen from "./GameOverScreen";

const INITIAL_VELOCITY = 3;
const PADDLE_SPEED = 4;
const SPEED_FACTOR = 0.5;

const BrickBreaker = () => {
  const [paddlePosition, setPaddlePosition] = useState(250);
  const [ballPosition, setBallPosition] = useState({ x: 290, y: 290 });
  const [ballVelocity, setBallVelocity] = useState({ dx: INITIAL_VELOCITY, dy: -INITIAL_VELOCITY });
  const [bricks, setBricks] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lives, setLives] = useState(3);
  const [isPaused, setIsPaused] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const gameContainerRef = useRef(null);
  const uid = auth.currentUser ? auth.currentUser.uid : null;
  const paddleRef = useRef(null);

  useEffect(() => {
    if (!gameOver && lives > 0 && !showPopup) {
      const popupTimer = setTimeout(() => {
        setShowPopup(true);
        setIsPaused(true);
      }, 15000);

      return () => clearTimeout(popupTimer);
    }
  }, [gameOver, lives, showPopup]);

  useEffect(() => {
    const initializeBricks = (rows = 5, pattern = "rectangular") => {
      const container = gameContainerRef.current;

      if (!container) return;

      const containerWidth = container.offsetWidth;
      const brickWidth = 75; // Set a fixed brick width
      const brickHeight = 20;
      const brickPadding = 10;
      const offsetTop = 30;

      // Calculate the number of columns that fit within the container width
      const columns = Math.floor(
        (containerWidth + brickPadding) / (brickWidth + brickPadding)
      );

      // Calculate the offset to center the bricks
      const offsetLeft =
        (containerWidth -
          columns * (brickWidth + brickPadding) +
          brickPadding) /
        2;

      const newBricks = [];

      switch (pattern) {
        case "rectangular":
          for (let c = 0; c < columns; c++) {
            for (let r = 0; r < rows; r++) {
              newBricks.push({
                x: c * (brickWidth + brickPadding) + offsetLeft,
                y: r * (brickHeight + brickPadding) + offsetTop,
                status: 1,
              });
            }
          }
          break;

        case "pyramid":
          for (let r = 0; r < rows; r++) {
            const bricksInRow = r + 1;
            const startCol = Math.floor((columns - bricksInRow) / 2);
            for (let c = startCol; c < startCol + bricksInRow; c++) {
              newBricks.push({
                x: c * (brickWidth + brickPadding) + offsetLeft,
                y: r * (brickHeight + brickPadding) + offsetTop,
                status: 1,
              });
            }
          }
          break;

        case "checkerboard":
          for (let c = 0; c < columns; c++) {
            for (let r = 0; r < rows; r++) {
              if ((c + r) % 2 === 0) {
                newBricks.push({
                  x: c * (brickWidth + brickPadding) + offsetLeft,
                  y: r * (brickHeight + brickPadding) + offsetTop,
                  status: 1,
                });
              }
            }
          }
          break;

        case "random":
          for (let c = 0; c < columns; c++) {
            for (let r = 0; r < rows; r++) {
              if (Math.random() > 0.5) {
                newBricks.push({
                  x: c * (brickWidth + brickPadding) + offsetLeft,
                  y: r * (brickHeight + brickPadding) + offsetTop,
                  status: 1,
                });
              }
            }
          }
          break;

        default:
          // Default to rectangular pattern
          for (let c = 0; c < columns; c++) {
            for (let r = 0; r < rows; r++) {
              newBricks.push({
                x: c * (brickWidth + brickPadding) + offsetLeft,
                y: r * (brickHeight + brickPadding) + offsetTop,
                status: 1,
              });
            }
          }
      }

      setBricks(newBricks);
    };

    const patterns = ["rectangular", "pyramid", "checkerboard", "random"];
    const pattern = patterns[Math.floor(Math.random() * patterns.length)];
    const randomRows = Math.floor(Math.random() * (10 - 5 + 1)) + 5;

    initializeBricks(randomRows, pattern);
  }, [level]);

  const keys = useRef({ left: false, right: false });

  useEffect(() => {
    if (gameOver || isPaused) {
      return;
    }

    let animationFrameId;

    const updateGame = () => {
      if (gameOver || isPaused) {
        return;
      }

      const container = gameContainerRef.current;
      const paddleContainer = paddleRef.current;
      const brickOffest = 5;
      if (!container || !paddleContainer) return;

      // Update paddle position
      // setPaddlePosition((prev) => {
      //   let newPosition = prev;
      //   if (keys.current.left) newPosition = Math.max(prev - PADDLE_SPEED, 0); // Smooth movement
      //   if (keys.current.right)
      //     newPosition = Math.min(
      //       prev + PADDLE_SPEED,
      //       container.offsetWidth - paddleContainer.offsetWidth
      //     );
      //   return newPosition;
      // });

      // Update ball position
      let { x, y } = ballPosition;
      let { dx, dy } = ballVelocity;

      // Ball collision with walls
      if (x + dx > container.offsetWidth - 10 || x + dx < 0) dx = -dx;
      if (y + dy < 0) dy = -dy;

      // Ball collision with paddle
      if (
        y + dy > container.offsetHeight - 40 &&
        x > paddlePosition &&
        x < paddlePosition + paddleContainer.offsetWidth
      ) {
        dy = -dy;
      }

      // Ball collision with bricks
      let allBricksCleared = true;
      for (let i = 0; i < bricks.length; i++) {
        const b = bricks[i];
        if (b.status === 1) {
          allBricksCleared = false;
          if (
            x > b.x - brickOffest &&
            x < b.x + 75 + brickOffest &&
            y > b.y - brickOffest &&
            y < b.y + 20 + brickOffest
          ) {
            dy = -dy;
            b.status = 0;
            setBricks([...bricks]);
            setScore(score + 10);
          }
        }
      }

      // Check if all bricks are cleared
      if (gameOver || isPaused) {
        return;
      }
      if (allBricksCleared) {
        setLevel(prevLevel => {
          console.log("Level:", prevLevel);
          const nextLevel = prevLevel + 1;
          setBallPosition({ x: 290, y: 290 });
          setBallVelocity({ dx: INITIAL_VELOCITY * nextLevel * SPEED_FACTOR, dy: -INITIAL_VELOCITY * nextLevel * SPEED_FACTOR });
          return nextLevel;
        });
        return;
      }      

      // Check if the ball hits the bottom
      if (y + dy > container.offsetHeight - 15 || y > paddleContainer.offsetTop) {
        setLives((prevLives) => {
          const updatedLives = prevLives - 1;

          if (updatedLives <= 0) {
            setGameOver(true);
            console.log("gameover", gameOver);
            gameOverDBUpdate();
          } else {
            setBallPosition({ x: 290, y: 290 });
            setBallVelocity({ dx: INITIAL_VELOCITY, dy: -INITIAL_VELOCITY });
          }
          return updatedLives;
        });
      }

      // Update ball position
      setBallPosition({ x: x + dx, y: y + dy });
      setBallVelocity({ dx, dy });

      // Request the next frame
      animationFrameId = requestAnimationFrame(updateGame);
    };

    // Start the game loop
    animationFrameId = requestAnimationFrame(updateGame);

    // Cleanup
    return () => cancelAnimationFrame(animationFrameId);
  }, [
    ballPosition,
    ballVelocity,
    paddlePosition,
    bricks,
    score,
    level,
    gameOver,
    isPaused,
    lives,
  ]);

  useEffect(() => {
    const keys = { left: false, right: false }; // Moved inside useEffect to avoid shared state
  
    const handleKeyDown = (e) => {
      if (isPaused || gameOver) return;
  
      if (e.key === "ArrowLeft") keys.left = true;
      if (e.key === "ArrowRight") keys.right = true;
    };
  
    const handleKeyUp = (e) => {
      if (e.key === "ArrowLeft") keys.left = false;
      if (e.key === "ArrowRight") keys.right = false;
    };
  
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
  
    let animationId;
    const updatePaddlePosition = () => {
      if (gameOver || isPaused) {
        animationId = requestAnimationFrame(updatePaddlePosition);
        return;
      }
  
      const container = gameContainerRef.current;
      if (!container) {
        animationId = requestAnimationFrame(updatePaddlePosition);
        return;
      }
  
      const paddleWidth = paddleRef.current.offsetWidth;
      setPaddlePosition((prev) => {
        let newPosition = prev;
        if (keys.left) newPosition = Math.max(prev - PADDLE_SPEED, 0);
        if (keys.right) newPosition = Math.min(prev + PADDLE_SPEED, container.offsetWidth - paddleWidth);
        return newPosition;
      });
  
      animationId = requestAnimationFrame(updatePaddlePosition);
    };
  
    // Start the animation loop
    animationId = requestAnimationFrame(updatePaddlePosition);
  
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      cancelAnimationFrame(animationId);
    };
  }, [isPaused, gameOver]);

  async function checkGameStarted() {
    const queryParams = new URLSearchParams(window.location.search);
    const gameId = queryParams.get("id");
    if (!gameId) return;

    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;

    try {
      const gameDoc = await getDocument(subCollectionPath, gameId);
      if (gameDoc?.gameStarted) {
        const redirectUrl = `${window.location.origin}/instructions/2048`;
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error("Error checking game status:", error);
    }
  }

  async function gameOverDBUpdate() {
    const queryParams = new URLSearchParams(window.location.search);
    var gameId = queryParams.get("id");
    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;
    let currentScore = score;
    const gameplayData = {
      lives: lives,
      score: currentScore,
    };
    await updateDocument(subCollectionPath, gameId, gameplayData);
    await totalGameDataUpdate(currentScore);
  }

  async function totalGameDataUpdate(score) {
    const subCollectionPath = `gameplay`;
    const userId = auth.currentUser.uid;
    var docData = await getDocument(subCollectionPath, userId);
    let oldScore = docData.score;
    let total_games = docData.games_played + 1;
    let currentScore = score + oldScore;
    const gameplayData = {
      games_played: total_games,
      score: currentScore,
    };
    await updateDocument(subCollectionPath, userId, gameplayData);
  }

  const handleClosePopup = () => {
    setShowPopup(false);
    setIsPaused(false);
  };

  const handleIncorrectAnswer = () => {
    if (lives > 0) {
      setLives((prevLives) => prevLives - 1);
    }
    if (lives - 1 <= 0) {
      setGameOver(true);
    }
  };

  const restartGame = () => {
    console.log("Restarting game");
    setScore(0);
    setLevel(1);
    setLives(3);
    setBallPosition({ x: 290, y: 290 });
    setBallVelocity({ dx: INITIAL_VELOCITY, dy: -INITIAL_VELOCITY });
    // setBricks([]);
    setIsPaused(false);
    setGameOver(false);
  };



  const togglePause = () => {
    setIsPaused((prev) => !prev);
  };

  // const renderHearts = () => {
  //   return Array.from({ length: 3 }).map((_, index) => (
  //     <span
  //       key={index}
  //       className="heart"
  //       style={{ opacity: index < lives ? 1 : 0.3 }}
  //     >
  //       ❤️
  //     </span>
  //   ));
  // };

  const exitGame = () => {
    window.location.href = "/games";
  };

  const renderHearts = () => {
    return Array.from({ length: 3 }).map((_, index) => (
      <span
        key={index}
        className={`text-red-500 text-2xl ${
          index < lives ? "opacity-100" : "opacity-30"
        }`}
      >
        ❤️
      </span>
    ));
  };

  return (
    <div className="min-h-screen bg-[var(--background-color)] py-8 px-4 sm:px-6 lg:px-8 animate-fadeIn">
      <div className="max-w-7xl mx-auto">
        {/* Game Title with Gradient */}
        <h1 className="font-['Jersey_25',sans-serif] text-4xl md:text-5xl font-bold text-center mb-10 text-yellow-500 bg-gradient-to-r from-[var(--primary-color)] via-[var(--interactive-color)] to-[var(--accent-color)] text-transparent bg-clip-text transform transition-all">
          Brick Breaker
        </h1>

        {/* Game Stats and Controls */}
        <div className="bg-[var(--surface-color)] rounded-2xl shadow-lg overflow-hidden border border-[var(--border-color)]">
          <div className="bg-[var(--primary-color)] py-4 px-6">
            <div className="flex items-center">
              <div className="text-white text-3xl w-full flex justify-between items-center p-4">
                <div className="flex items-center gap-6">
                  <h1 className="text-2xl font-bold text-[var(--text-color)]">
                    Score: {score}
                  </h1>
                  <h1 className="text-2xl font-bold text-[var(--text-color)]">
                    Lives: {renderHearts()}
                  </h1>
                  <h1 className="text-2xl font-bold text-[var(--text-color)]">
                    Level: {level}
                  </h1>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    className="group relative inline-flex items-center px-6 py-3 bg-[var(--interactive-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                    onClick={restartGame}
                  >
                    <MdOutlineRestartAlt className="mr-2 text-xl" />
                    Restart
                  </button>

                  <button
                    className="group relative inline-flex items-center px-6 py-3 bg-[var(--interactive-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                    onClick={togglePause}
                  >
                    {isPaused ? (
                      <FaPlay className="mr-2 text-white group-hover:animate-pulse" />
                    ) : (
                      <FaPause className="mr-2 text-white group-hover:animate-pulse" />
                    )}
                    {isPaused ? "Resume" : "Pause"}
                  </button>

                  <button
                    className="group relative inline-flex items-center px-6 py-3 bg-[var(--error-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--error-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                    onClick={exitGame}
                  >
                    Exit
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Game Container */}
          <div
            className="w-full max-w-4xl h-[600px] relative border-4 border-[var(--border-color)] rounded-lg bg-[var(--surface-color)] overflow-hidden mx-auto"
            ref={gameContainerRef}
          >
            {/* Paddle */}
            <div
              className="absolute bottom-2 h-3 bg-blue-500 rounded-md"
              style={{ left: paddlePosition, width: "15%" }}
              ref={paddleRef}
            ></div>

            {/* Ball */}
            <div
              className="absolute w-4 h-4 bg-red-500 rounded-full"
              style={{ left: ballPosition.x, top: ballPosition.y }}
            ></div>

            {/* Bricks */}
            {bricks.map((brick, index) =>
              brick.status === 1 ? (
                <div
                  key={index}
                  className="absolute w-[75px] h-5 bg-[var(--primary-color)]"
                  style={{ left: brick.x, top: brick.y }}
                ></div>
              ) : null
            )}
          </div>
        </div>

        {/* Popup and Game Over Screens */}
        {showPopup && (
          <Popup
            onClose={handleClosePopup}
            score={score}
            setScore={setScore}
            lives={lives}
            setLives={setLives}
            useLives={true}
          />
        )}

        {gameOver && (
          <GameOverScreen
            score={score}
            onRestart={restartGame}
            onExit={exitGame}
          />
        )}
      </div>
    </div>
  );
};

export default BrickBreaker;
